import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';
import { Field, controlClasses, describedBy } from './Field';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  containerClassName?: string;
  /** Shows a live "n / max" counter. Requires `maxLength`. */
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    hideLabel,
    containerClassName,
    showCount,
    className,
    id: providedId,
    required,
    rows = 4,
    maxLength,
    value,
    ...props
  },
  ref,
) {
  const id = useFieldId(providedId);
  const length = typeof value === 'string' ? value.length : 0;

  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      error={error}
      required={required}
      hideLabel={hideLabel}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        maxLength={maxLength}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(controlClasses(Boolean(error)), 'py-2 leading-relaxed', className)}
        {...props}
      />
      {showCount && maxLength && (
        <p className="text-right text-xs text-ink-subtle" aria-live="polite">
          {length} / {maxLength}
        </p>
      )}
    </Field>
  );
});
